/**
 * Guided path for creating strategy and clarity objects in a sensible order.
 * Route: /prioritization/strategy/guided-setup
 */

import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Compass,
  CircleDot,
  Target,
  TreeDeciduous,
  Flag,
  Crosshair,
  Map,
  ListTree,
  HelpCircle,
  Lightbulb,
  LayoutTemplate,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { StrategyClarityMap } from './StrategyClarityMap';
import {
  StrategyConnectivityHelpButton,
} from './StrategyConnectivityDocModal';
import type { StrategyConnectivityContext } from './strategyConnectivityTypes';

interface GuideStep {
  step: number;
  title: string;
  summary: string;
  detail: string;
  href: string;
  cta: string;
  /** Context for the per-step connectivity modal (same as other strategy pages). */
  connectivityContext: StrategyConnectivityContext;
  optional?: boolean;
}

const CORE_STEPS: GuideStep[] = [
  {
    step: 1,
    title: 'Outcome driver',
    summary: 'Name why change is needed',
    detail:
      'Capture context, current facts, the gap or opportunity, and why it matters. This is the “why” that everything else can trace back to.',
    href: '/prioritization/outcome-drivers/new',
    cta: 'Start with a new outcome driver',
    connectivityContext: 'outcome_driver_new',
  },
  {
    step: 2,
    title: 'Desired outcome',
    summary: 'Define who wants what and what success looks like',
    detail:
      'On the list page, choose New Desired Outcome, name it, then answer the seven-question outcome narrative so teams share one picture of success before you commit to execution paths.',
    href: '/prioritization/desired-outcomes',
    cta: 'Open Desired Outcomes',
    connectivityContext: 'desired_outcomes',
  },
  {
    step: 3,
    title: 'Strategy (classic)',
    summary: 'Create the alignment container',
    detail:
      'A classic strategy holds vision, mission, and related context. Objectives and goals will hang under this row.',
    href: '/prioritization/strategy/new?framework=classic',
    cta: 'Create a strategy',
    connectivityContext: 'strategy_create',
  },
  {
    step: 4,
    title: 'Objectives',
    summary: 'Directional outcomes under the strategy',
    detail:
      'Strategic objectives express what the strategy is trying to achieve; they bridge narrative and measurable work.',
    href: '/prioritization/strategy/objectives',
    cta: 'Add or manage objectives',
    connectivityContext: 'objectives',
  },
  {
    step: 5,
    title: 'Goals',
    summary: 'Concrete targets under objectives',
    detail:
      'Goals sharpen objectives into specific, trackable commitments for alignment and prioritization views.',
    href: '/prioritization/strategy/goals',
    cta: 'Add or manage goals',
    connectivityContext: 'goals',
  },
];

const BRANCH_STEPS: GuideStep[] = [
  {
    step: 6,
    title: 'Choose how you will execute',
    summary: 'Pick one or both paths after the desired outcome',
    detail:
      'Back plans reason backward from success. Opportunity solution trees explore opportunities and experiments from a root outcome. Both can link to prioritization items.',
    href: '/prioritization/backplan',
    cta: 'Open Back plans',
    connectivityContext: 'backplan_list',
    optional: true,
  },
  {
    step: 6,
    title: '…or continuous discovery',
    summary: 'Opportunity solution tree',
    detail:
      'From the Strategy hub you can open or start a tree. Root your outcome, then branch into opportunities, solutions, and experiments.',
    href: '/prioritization/strategy',
    cta: 'Go to Strategy hub (OST)',
    connectivityContext: 'opportunity_trees_list',
    optional: true,
  },
];

function StepCard({ item, variant }: { item: GuideStep; variant: 'core' | 'branch' }) {
  const isBranch = variant === 'branch';
  return (
    <div
      className={`relative  border p-5 sm:p-6 ${
        isBranch
          ? 'border-[var(--rule)] bg-[var(--bg-raised)]/60 dark:bg-[color-mix(in_srgb,var(--surface)_80%,transparent)]'
          : 'border-primary-200 dark:border-primary-900/50 bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-sm'
      }`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-4 min-w-0">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${
              isBranch
                ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                : 'bg-primary-100 dark:bg-primary-950/50 text-primary-800 dark:text-primary-200'
            }`}
          >
            {item.step}
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {item.title}
            </h2>
            <p className="mt-0.5 text-sm font-medium text-primary-700 dark:text-primary-300">
              {item.summary}
            </p>
            <p className="mt-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
              {item.detail}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:items-end sm:pl-4 flex-shrink-0">
          <Link
            to={item.href}
            className="inline-flex items-center justify-center gap-2 bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
          >
            {item.cta}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <StrategyConnectivityHelpButton
            context={item.connectivityContext}
            className="w-full sm:w-auto justify-center"
            label={
              <span className="text-xs sm:text-sm">
                <span className="hidden sm:inline">How this connects</span>
                <span className="sm:hidden">Map</span>
              </span>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default function StrategyObjectsGuidePage() {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <PageHeader
        icon={<Compass className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
        iconBg="bg-primary-50 dark:bg-primary-950/40"
        title="Guided setup"
        subtitle="Create strategy objects in order — from outcome driver through goals — using the same map as everywhere else in Strategy."
        actions={<StrategyConnectivityHelpButton context="strategy_objects_guide" />}
        className="max-w-none"
        mb=""
      />

      <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3 text-sm dark:border-[var(--mustard)]/80">
        <p className="font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">First time here?</p>
        <p className="mt-1 text-[var(--mustard)] leading-relaxed">
          Work the numbered steps in order. On each workspace screen, use{' '}
          <span className="font-semibold text-[var(--mustard)]">How this connects</span>{' '}
          (top right) for the methodology map
          focused on that object — the same guide as on Objectives, Outcome Drivers, and other pages.
        </p>
      </div>

      <StrategyClarityMap showIntro />

      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
          Recommended order
        </h2>
        <ol className="space-y-4 list-none p-0 m-0">
          {CORE_STEPS.map((s) => (
            <li key={`${s.step}-${s.title}`}>
              <StepCard item={s} variant="core" />
            </li>
          ))}
        </ol>
      </div>

      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1">
          Then branch (parallel)
        </h2>
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-3 max-w-2xl">
          After you have a desired outcome, choose discovery and planning tracks. Many teams use one; some use both.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {BRANCH_STEPS.map((s) => (
            <StepCard key={s.title} item={s} variant="branch" />
          ))}
        </div>
      </div>

      <div className="border border-[var(--rule)] bg-[var(--surface)]/80 dark:bg-[var(--bg)]/30 p-5">
        <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-3">
          Anytime and optional lenses
        </h3>
        <ul className="space-y-3 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          <li className="flex gap-2">
            <HelpCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-[var(--text-ter)]" />
            <span>
              <Link
                to="/prioritization/open-questions"
                className="font-medium text-primary-600 hover:underline dark:text-primary-400"
              >
                Open questions
              </Link>
              {' — '}surface unknowns whenever they show up; not sequenced after the driver.
            </span>
          </li>
          <li className="flex gap-2">
            <LayoutTemplate className="h-4 w-4 flex-shrink-0 mt-0.5 text-[var(--text-ter)]" />
            <span>
              <Link
                to="/prioritization/strategy/canvas"
                className="font-medium text-primary-600 hover:underline dark:text-primary-400"
              >
                Strategy canvas
              </Link>
              {' — '}one-page story linked to strategies, objectives, and goals.
            </span>
          </li>
          <li className="flex gap-2">
            <Lightbulb className="h-4 w-4 flex-shrink-0 mt-0.5 text-[var(--text-ter)]" />
            <span>
              <Link
                to="/prioritization/strategy/kernel"
                className="font-medium text-primary-600 hover:underline dark:text-primary-400"
              >
                Strategy kernel
              </Link>
              {' — '}diagnosis, guiding policy, coherent action (Rumelt) alongside classic rows.
            </span>
          </li>
          <li className="flex gap-2">
            <ListTree className="h-4 w-4 flex-shrink-0 mt-0.5 text-[var(--green)]" />
            <span>
              <Link
                to="/prioritization/items"
                className="font-medium text-[var(--green)] hover:underline dark:text-[var(--text-ter)]"
              >
                Prioritization items
              </Link>
              {' — '}where work is tracked; methodology links connect back to drivers, outcomes, OST, and back plans.
            </span>
          </li>
        </ul>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        <span className="inline-flex items-center gap-1">
          <CircleDot className="h-3.5 w-3.5" /> Driver
        </span>
        <span className="inline-flex items-center gap-1">
          <Target className="h-3.5 w-3.5" /> Desired outcome
        </span>
        <span className="inline-flex items-center gap-1">
          <TreeDeciduous className="h-3.5 w-3.5" /> Strategy
        </span>
        <span className="inline-flex items-center gap-1">
          <Flag className="h-3.5 w-3.5" /> Objectives
        </span>
        <span className="inline-flex items-center gap-1">
          <Crosshair className="h-3.5 w-3.5" /> Goals
        </span>
        <span className="inline-flex items-center gap-1">
          <Map className="h-3.5 w-3.5" /> Back plan / OST
        </span>
      </div>
    </div>
  );
}
